import {
  createListenerMiddleware,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import { ApplicationBuildId, CloudRenderingRegion } from "../hooks/types";
import { RootState } from "../store";
import {
  ClosestRegionInfo,
  hasDistanceChangedSignificantly,
  loadClosestRegionInfo,
  persistClosestRegionInfo,
} from "../utils/closest-region-distance";
import { latencyLookup } from "../utils/latency-lookup";

type CloudRenderingState = {
  regionsLatency: { [id: string]: number };
  regionsLatencies: { [id: string]: number[] };
  regionsToTest: CloudRenderingRegion[];
  latencyTestRunning: boolean;
  latencyTestCompleted: boolean;
  configurationDialogShown: boolean;
  applicationBuildId?: ApplicationBuildId;
  numberOfRegionsToTest: number;
  numberOfRegionsTested: number;
  closestRegion?: Omit<ClosestRegionInfo, "timestamp"> | undefined;
};

type LatencyCheck = { region: string; latencyMs: number };

function delay(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line prefer-const
    let timeout: number;
    const handleAbort = () => {
      clearTimeout(timeout);
      reject(signal.reason);
    };
    signal.addEventListener("abort", handleAbort);
    timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, ms);
  });
}

const latencyPersistenceKey = "portal-latency";

function persistLatencyInformation(latency: { [id: string]: number }) {
  try {
    localStorage.setItem(latencyPersistenceKey, JSON.stringify(latency ?? {}));
  } catch {
    // ignore write errors
  }
}

function loadLatencyInformation(): { [id: string]: number } {
  try {
    const serializedState = localStorage.getItem(latencyPersistenceKey);
    if (!serializedState) {
      return {};
    }

    return JSON.parse(serializedState);
  } catch {
    /* empty */
  }
  return {};
}

/**
 * Reduces the raw latency samples for a region to a single representative value.
 *
 * The first sample is discarded: the initial request to a region pays for DNS +
 * TCP + TLS connection establishment (see #1213), which inflates it well beyond
 * the steady-state round-trip time. Subsequent requests reuse the warm connection
 * and reflect the actual latency. The remaining samples are reduced to their
 * median to reject the occasional outlier.
 *
 * Returns `undefined` while only the warm-up sample is available, i.e. there is
 * nothing meaningful to report yet.
 */
export function aggregateLatency(samples: number[]): number | undefined {
  // discard the first (connection-establishment) sample
  const measurements = samples.slice(1);
  if (measurements.length === 0) {
    return undefined;
  }

  const sorted = [...measurements].sort((a, b) => a - b);
  const half = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[half]
    : Math.floor((sorted[half - 1] + sorted[half]) / 2);
}

const cloudRenderingSlice = createSlice({
  name: "cloud-rendering",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: () => {
    const closestRegion = loadClosestRegionInfo();
    const initialState: CloudRenderingState = {
      regionsLatency: loadLatencyInformation(),
      regionsLatencies: {},
      regionsToTest: [],
      latencyTestRunning: false,
      latencyTestCompleted: false,
      configurationDialogShown: false,
      numberOfRegionsToTest: 0,
      numberOfRegionsTested: 0,
      closestRegion,
    };

    if (Object.keys(initialState.regionsLatency).length !== 0) {
      initialState.latencyTestCompleted = true;
    }
    return initialState;
  },
  reducers: {
    openConfigurationDialog: (
      state,
      action: PayloadAction<ApplicationBuildId | undefined>,
    ) => {
      state.configurationDialogShown = true;
      state.applicationBuildId = action?.payload;
    },
    setRegionsToTest: (
      state,
      action: PayloadAction<CloudRenderingRegion[]>,
    ) => {
      state.latencyTestCompleted = false;
      state.latencyTestRunning = true;
      state.regionsToTest = action.payload;
      state.numberOfRegionsToTest = action.payload.length;
      state.numberOfRegionsTested = 0;
    },
    resetTestRegions: (state) => {
      state.latencyTestCompleted = false;
      state.latencyTestRunning = false;
      state.regionsLatency = {};
      state.regionsLatencies = {};
    },
    closeConfigurationDialog: (state) => {
      state.configurationDialogShown = false;
    },
    addLatency: (state, action: PayloadAction<LatencyCheck>) => {
      const { region, latencyMs } = action.payload;
      const latencies = state.regionsLatencies[region] ?? [];

      latencies.push(latencyMs);
      state.regionsLatencies[region] = latencies;

      // discards the connection-establishment warm-up and takes the median
      const latency = aggregateLatency(latencies);
      if (latency !== undefined) {
        state.regionsLatency[region] = latency;
      }
    },
    completeLatencyTest: (state) => {
      state.latencyTestCompleted = true;
      state.latencyTestRunning = false;
      state.regionsToTest = [];
      state.numberOfRegionsToTest = 0;
      state.numberOfRegionsTested = state.numberOfRegionsToTest;
    },
    abortLatencyTest: (state) => {
      state.latencyTestCompleted = false;
      state.latencyTestRunning = false;
      state.regionsToTest = [];
      state.numberOfRegionsToTest = 0;
      state.numberOfRegionsTested = state.numberOfRegionsToTest;
    },
    removeRegionToTest: (state, action: PayloadAction<string>) => {
      const regionIndex = state.regionsToTest.findIndex(
        (region) => region.name === action.payload,
      );
      if (regionIndex !== -1) {
        state.regionsToTest.splice(regionIndex, 1);
        // update the test progress
        state.numberOfRegionsTested += 1;
      }
    },
    updateClosestRegionInfo: (
      state,
      action: PayloadAction<Omit<ClosestRegionInfo, "timestamp">>,
    ) => {
      const newDistanceM = action.payload.distanceM;
      const previousClosestRegionInfo = state.closestRegion;

      // Check if distance changed significantly (more than 100km)
      if (
        hasDistanceChangedSignificantly(
          previousClosestRegionInfo?.distanceM ?? null,
          newDistanceM,
        ) ||
        previousClosestRegionInfo?.name !== action.payload.name
      ) {
        // Invalidate latency cache
        state.regionsLatency = {};
        state.regionsLatencies = {};
        state.latencyTestCompleted = false;
      }

      // Update the stored distance
      state.closestRegion = action.payload;
    },
  },
});

export const {
  openConfigurationDialog,
  resetTestRegions,
  setRegionsToTest,
  closeConfigurationDialog,
  addLatency,
  completeLatencyTest,
  removeRegionToTest,
  abortLatencyTest,
  updateClosestRegionInfo,
} = cloudRenderingSlice.actions;

// Middleware to start latency checks and save the latency info to local storage
const listenerMiddleware = createListenerMiddleware<{
  cloudRendering: CloudRenderingState;
}>();

listenerMiddleware.startListening({
  matcher: isAnyOf(setRegionsToTest, abortLatencyTest),
  effect: async (_action, listenerApi) => {
    // cancel any previously running tasks
    listenerApi.cancelActiveListeners();

    // start the latency test
    const cloudRenderingState = listenerApi.getState().cloudRendering;
    const regionsToTest = cloudRenderingState.regionsToTest;

    if (regionsToTest.length === 0) {
      return;
    }

    // the latency lookups need to be made sequentially to avoid network clogging
    for (const region of regionsToTest) {
      if (listenerApi.signal.aborted) {
        // abort if requested
        return;
      }

      try {
        // wait a bit to avoid network cloging
        await delay(200, listenerApi.signal);

        const latency = await latencyLookup(region);

        listenerApi.dispatch(
          addLatency({ latencyMs: latency, region: region.name }),
        );

        // ignore the result if the document is not visible right now and schedule to continue the test later on
        if (document.visibilityState !== "visible") {
          // pause the test until the tab is visible again
          const waitForVisbility = new Promise<void>((resolve, reject) => {
            listenerApi.signal.addEventListener("abort", reject);
            document.addEventListener(
              "visibilitychange",
              () => {
                if (document.visibilityState === "visible") {
                  resolve();
                }
              },
              { once: true },
            );
          });
          await listenerApi.pause(waitForVisbility);
          // delay execution a bit to prevent interference with other requests
          await delay(500, listenerApi.signal);
        }
      } catch {
        // could not determine the latency to that region, remove it from the regions to test (see finally block)
      }
      if (listenerApi.signal.aborted) {
        // abort if requested
        return;
      }
      // mark the region as tested
      listenerApi.dispatch(removeRegionToTest(region.name));
    }

    listenerApi.dispatch(completeLatencyTest());
  },
});

listenerMiddleware.startListening({
  actionCreator: completeLatencyTest,
  effect: (_action, listenerApi) => {
    // persist latency information to localstorage
    const state = listenerApi.getState() as RootState;
    persistLatencyInformation(state.cloudRendering.regionsLatency);
  },
});

listenerMiddleware.startListening({
  actionCreator: resetTestRegions,
  effect: () => {
    // clear the stored latency information from localstorage
    persistLatencyInformation({});
  },
});

listenerMiddleware.startListening({
  actionCreator: updateClosestRegionInfo,
  effect: (_action, listenerApi) => {
    // persist closest region info to localstorage
    const state = listenerApi.getState();
    const closestRegion = state.cloudRendering.closestRegion;

    if (closestRegion !== undefined) {
      persistClosestRegionInfo(closestRegion);
    }
  },
});

export const latencyCheckMiddleware = listenerMiddleware.middleware;

export const selectCloudRendering = (state: RootState) => state.cloudRendering;
export const selectRegionsLatency = createSelector(
  selectCloudRendering,
  (cloudRendering) => cloudRendering.regionsLatency,
);
export const selectLatencyTestProgress = createSelector(
  selectCloudRendering,
  (cloudRendering) =>
    cloudRendering.numberOfRegionsToTest === 0
      ? 0
      : cloudRendering.numberOfRegionsTested /
        cloudRendering.numberOfRegionsToTest,
);

export const selectLatencyTestCompleted = createSelector(
  selectCloudRendering,
  (cloudRendering) => cloudRendering.latencyTestCompleted,
);

export default cloudRenderingSlice.reducer;
